<template>

<div class="list-view">

	<div v-if="showDetail" class="sub-header detail-sub-header">
	</div>
	<div v-else class="sub-header">
		<div class="input-with-control">
			<input type="text" ref="search_term" v-model="filters.search_term" placeholder="Search..." @keyup.enter="getList()" v-focus>
			<div class="button action input-go" @click="getList()"><i class="fas fa-search"></i></div>
		</div>
		<div class="controls">
			<pagination :paging="paging" @updatePage="goToPage"></pagination>
		</div>
	</div>

	<div class="filters">

		<div v-if="showDetail">
			<div :class="['button', canSave ? 'action' : 'disabled']" @click="saveItem()">Save Rental</div>
			<div class="button neutral" @click="backToList()">Back To List</div>
		</div>
		<div v-else>
			<ul class="checkbox-group">
				<li>
					<span :class="['pointer', 'far', filters.internal_use_only ? 'fa-check-square' : 'fa-square']" @click="toggleFilter('internal_use_only')"></span>
					<label>Hidden</label>
				</li>
				<li>
					<span :class="['pointer', 'far', filters.inactive ? 'fa-check-square' : 'fa-square']" @click="toggleFilter('inactive')"></span>
					<label>Inactive</label>
				</li>
			</ul>
			<div class="divider"></div>
			<div class="button action" @click="addItem()">Add a Rental</div>
			<div :class="['extra-menu-item', gettingExport ? 'disabled' : 'neutral']" @click="doExport()">Export CSV</div>
        	<div v-if="getDevEnvironment" class="extra-menu-item" @click="getList()">Reload List</div>

			<div v-if="legacyLink">
				<div class="divider"></div>
				<router-link :to="legacyLink" class="extra-menu-item">Legacy Version</router-link>
			</div>

		</div>
	</div>

	<loading v-if="loading" class="loading"></loading>

	<div v-else-if="showDetail" class="detail">
		<div v-if="!listDetail.product_id" class="two-columns" style="margin: 0 0 1em 0; padding: 0 0 0.5em 0; border-bottom: 1px solid #ccc;">
			<div>Want to clone an existing product to create a new rental? Search products to pre-fill description and other data.</div>
			<div>
				<div class="input-with-control">
					<input type="text" ref="search_term" v-model="filters.product_search_term" placeholder="Search..." @keyup.enter="searchProducts()" v-focus>
					<div class="button action input-go" @click="searchProducts()"><i class="fas fa-search"></i></div>
				</div>
			</div>
		</div>
		<div class="three-one-columns">
			<div>
				<h2>Item Details</h2>
				<div class="two-columns" style="margin: 0 0 1em 0;">
					<div>
						<label-required :label="'UPC'" :condition="listDetail.upc" /><span class="note" style="padding: 0 0 0 0.5em;">must be unique for this rental</span>
						<input type="text" v-model="listDetail.upc">
						<label>Cost</label>
						<input type="text" v-model="listDetail.cost">
						<label-required :label="'POS Price'" :condition="listDetail.pos_price" />
						<input type="text" v-model="listDetail.pos_price">
						<label>Serial Number or SKU</label>
						<input type="text" v-model="listDetail.sku">
						<div style="margin: 0.75em 0;">
							<checkbox v-model="listDetail.not_taxable" label="This rental is not taxable" />
						</div>
						<div style="margin: 0.75em 0;">
							<checkbox v-model="listDetail.internal_use_only" label="Hidden"/>
						</div>
						<div style="margin: 0.75em 0;">
							<checkbox v-model="listDetail.inactive" label="Inactive"/>
						</div>
					</div>
					<div>
						<label-required :label="'Description'" :condition="listDetail.description" />
						<textarea v-model="listDetail.description" style="height: 6em;"></textarea>
						<label>Model</label>
						<input type="text" v-model="listDetail.model">
						<div v-if="listDetail.product_id">
							<label>Caliber</label>
							<div class="product-data">{{ listDetail.caliber || 'N/A' }}</div>
							<label>Manufacturer</label>
							<div class="product-data">{{ listDetail.manufacturer || 'N/A' }}</div>
							<div style="margin: 0.75em 0 0 0;">
								<div class="note">Need to change caliber, manufacturer or image? You can use the Manage Products program.</div>
								<router-link :to="'/inventory/manage-products/detail/' + listDetail.product_id" class="button neutral inline">Manage This Product</router-link>
							</div>
						</div>
					</div>
				</div>

				<h2>Associated Serial Numbers</h2>
				<div class="two-columns" style="grid-column-gap: 3em;">
					<div>
						<div class="message" v-if="listDetail.serial_numbers.length == 0">
							No serial numbers currently associated.
						</div>
						<div v-else>
							<div v-for="(result, index) in listDetail.serial_numbers" class="list-item" style="padding: 0.5em;">
								<div style="float: right; margin-left: 1em;">
									<div class="button smaller neutral" @click="listDetail.serial_numbers.splice(index, 1)">X</div>
								</div>
								<div class="one-two-columns align-center">
									<label>Serial Number</label><span>{{ result.serial_number }}</span>
									<label>Sequence</label><span>{{ result.ffl_log_book_code }} - {{ result.sequence_number }}</span>
									<label>Gun Type</label><span>{{ result.gun_type }}</span>
									<label>Manufacturer</label><span>{{ result.manufacturer }}</span>
									<label>Model</label><span>{{ result.model }}</span>
									<label>Caliber</label><span>{{ result.caliber }}</span>

									<label></label><span>&nbsp;</span>
									<label>Finish</label><span>
										<select v-model="result.finish">
											<option value="">Select...</option>
											<option value="black">Black</option>
											<option value="blue_steel">Blue Steel</option>
											<option value="nickel">Nickel</option>
											<option value="stainless_steel">Stainless Steel</option>
											<option value="two_toned">Two-Toned</option>
											<option value="cerakote">Cerakote</option>
											<option value="other">Other</option>
										</select>
									</span>

									<label v-if="result.finish == 'two_toned'">Tone 1</label>
									<input v-if="result.finish == 'two_toned'" type="text" v-model="result.finish_two_toned_1">
									<label v-if="result.finish == 'two_toned'">Tone 2</label>
									<input v-if="result.finish == 'two_toned'" type="text" v-model="result.finish_two_toned_2">

									<label v-if="result.finish == 'cerakote'">Color</label>
									<input v-if="result.finish == 'cerakote'" type="text" v-model="result.finish_cerakote">

									<label v-if="result.finish == 'other'">Other Finish</label>
									<input v-if="result.finish == 'other'" type="text" v-model="result.finish_other">

								</div>
							</div>
						</div>
					</div>
					<div>
						<div class="message" style="margin: 1em 0;">
							Search your logbooks for on-hand serial numbers to associate with this rental...
						</div>
						<div class="input-with-control">
							<input type="search" v-model="serialNumberSearch" @search="searchSerialNumbers()" placeholder="Serial number...">
							<div class="button action input-go" @click="searchSerialNumbers()"><i class="fas fa-search"></i></div>
						</div>
						<div class="message v-spaced" v-if="serialNumberSearchMessage">{{ serialNumberSearchMessage }}</div>
						<div v-if="serialNumberSearchResults.length > 0" class="serial-number-results">
							<div v-for="(result, index) in serialNumberSearchResults" class="clickable-list-item" style="padding: 0.5em;" @click="assignSerialNumber(result)">
								<div class="one-two-columns">
									<label>Serial Number</label><span>{{ result.serial_number }}</span>
									<label>Sequence</label><span>{{ result.ffl_log_book_code }} - {{ result.sequence_number }}</span>
									<label>Gun Type</label> {{ result.gun_type }}
									<label>Manufacturer</label> {{ result.manufacturer }}
									<label>Model</label> {{ result.model }}
									<label>Caliber</label> {{ result.caliber }}
								</div>
							</div>
						</div>
					</div>
				</div>

			</div>
			<div class="center">
				<div v-if="listDetail.image_src">
					<img :src="listDetail.image_src" style="width: auto; max-height: 11em; max-width: 15em;" />
				</div>
			</div>
		</div>
	</div>

	<div v-else class="list">

	    <div v-if="list.length == 0" class="empty-list">
	        This list is empty
	    </div>

        <table v-else class="data-list" cellpadding="0" cellspacing="0">
			<thead>
				<tr>
					<th class="list"></th>
					<th class="list">UPC</th>
					<th class="list">Description</th>
					<th class="list right">Price</th>
					<th class="list right">On Hand</th>
				</tr>
			</thead>
			<tr v-for="(item, index) in list" class="clickable-list-item" @click="getItem(item)">
                <td class="list tiny-image center"><img :src="item.image_src" style="max-width: 3em;"></td>
                <td class="list left">{{ item.upc }}</td>
                <td class="list left">{{ item.description }}</td>
                <td class="list right">{{ item.price }}</td>
                <td class="list right">{{ item.available }}</td>
            </tr>
        </table>

    </div>

	<div v-if="showRemoveRental" class="modal image-preview">
		<div class="inner">
			<h2>Confirm Remove Rental</h2>
			<div class="content">
				<p>If you are disposing this rental and don't plan to sell it as a used item, simply proceed with Remove Rental.</p>
				<p>However, if you would like to sell this rental as a used item, you can Manage This Product to move it to a sales department and category and adjust its price and availability.</p>
			</div>
			<div class="controls">
				<div class="button cancel spaced" @click="showRemoveRental = false">Cancel</div>
				<div class="button action spaced" @click="showRemoveRental = false">Remove Rental</div>
			</div>
		</div>
	</div>

	<div v-if="searchMessage" class="modal image-preview">
		<div class="inner">
			<h2>Search Products</h2>
			<div class="content">
				<p>{{ searchMessage }}</p>
				<div v-for="product in searchList" class="clickable-list-item one-three-columns" style="padding: 0.5em;" @click="cloneProduct(product)">
					<div>
						<img :src="product.image_src">
					</div>
					<div>
						{{ product.description }}<br>
						<label>Mfg</label> {{ product.manufacturer }}<br>
						<label>Model</label> {{ product.model }}<br>
					</div>
				</div>
			</div>
			<div class="controls">
				<div class="button cancel" @click="searchMessage = ''">Cancel</div>
			</div>
		</div>
	</div>

	<help-popup :helpLink="{59:'Add Rental Products', 326:'Range Checkout'}" :tours="false"></help-popup>

	<message-modal v-if="message" :message="message" :loading="saving" :errorStatus="messageStatus" :timeout="messageTimeout" @dismiss="message = ''" />

	<iframe id="download" src="" style="display: none;"></iframe>

</div>

</template>

<script>
import Loading from '@/components/Loading.vue'
import Checkbox from '@/components/Checkbox.vue'
import LabelRequired from '@/components/LabelRequired.vue'
import HelpPopup from '@/components/HelpPopup.vue'

export default {
	name: 'rentals',
	props: ['product_id'],
    components: {
        'loading': Loading,
		'checkbox': Checkbox,
		'label-required': LabelRequired,
		'help-popup': HelpPopup
    },
	data() {
		return {
            loading: false,
			filters: {},
			list: [],
			paging: {},
			showDetail: false,
			listDetail: {},
			// serial_numbers: [],
			serialNumberSearch: '',
			serialNumberSearchMessage: '',
			serialNumberSearchResults: [],
			gettingExport: false,
			showRemoveRental: false,
			saving: false,
			message: '',
			messageStatus: '',
			messageTimeout: 0,
			searching: false,
			searchList: [],
			searchMessage: '',
			legacyLink: ''
		}
	},
	created() {
		this.clearFilters()
	},
	computed: {
		canSave() {
			return this.listDetail.upc && this.listDetail.pos_price && this.listDetail.description
		}
	},
	methods: {
		clearFilters() {
			this.filters = {
				page: 1,
				search_term: '',
				internal_use_only: 0,
				inactive: 0,
				do_export: 0
			}
			this.getList()
		},
		getList() {
			if (this.loading) { return }
			this.loading = true
			this.apiCall({ destination: 'get_list_rental_products', data: this.filters }).then(obj => {
				this.loading = false
				if (this.filters.do_export) {
					this.filters.do_export = 0
					var outputFile = obj.file_name
					// accommodate live server filecache latency
					setTimeout(() => {
						document.getElementById("download").src = obj.download_url
						this.gettingExport = false
						this.exportMessage = ''
					}, 1500)
				} else {
					this.list = obj.list
					if (obj.paging) {
						this.paging = obj.paging
					}
					if (obj.legacy_link) {
						this.legacyLink = obj.legacy_link
					}
				}
			}).catch(err => {
				console.log(err)
			})
		},
		toggleFilter(which) {
			this.filters[which] = !this.filters[which]
			this.filters.page = 1
			this.getList()
		},
		goToPage(page) {
			this.filters.page = page
			this.getList()
		},
		getItem(item) {
			if (this.loading) { return }
			this.loading = true
			this.apiCall({ destination: 'get_item_rental_product', data: { product_id: item.product_id } }).then(obj => {
				this.loading = false
                if (obj.status == 'success') {
					this.listDetail = obj.detail
                    this.showDetail = true
                } else {
                    this.message = obj.message
                }
			}).catch(err => {
				console.log(err)
			})
		},
		addItem() {
			this.listDetail = {
				product_id: '',
				description: '',
				upc: '',
				cost: '',
				pos_price: '',
				sku: '',
				not_taxable: 0,
				internal_use_only: 0,
				inactive: 0,
				serial_numbers: []
			}
			this.showDetail = true
		},
		saveItem() {
			if (this.saving || !this.canSave) { return }
			this.saving = true
			this.message = 'Saving rental...'
			this.messageStatus = ''
			this.apiCall({ destination: 'save_item_rental_product', data: this.listDetail }).then(obj => {
				this.saving = false
				this.messageStatus = obj.status
                if (obj.status == 'success') {
					this.message = 'Rental saved!'
					this.messageTimeout = 2000
					if (!this.listDetail.product_id) {
						this.listDetail.product_id = obj.product_id
					}
					this.serialNumberSearchMessage = ''
					this.serialNumberSearchResults = []
					this.serialNumberSearch = ''
                } else {
					this.message = obj.message
				}
			}).catch(err => {
				console.log(err)
			})
		},
		backToList() {
			this.listDetail = {}
			this.showDetail = false
			this.getList()
		},
		doExport() {
			if (this.gettingExport) {
				return false
			}
			this.gettingExport = true
			this.exportMessage = 'Generating CSV for ' + this.list.length + ' items...'
			this.filters.product_id = ''
			this.filters.do_export = true
			this.getList()
		},
		searchProducts() {
			if (this.searching) { return }
			this.searchList = []
			this.searching = true
			this.searchMessage = 'Searching...'
			let productFilters = {
				page: 1,
				search_term: this.filters.product_search_term,
				image_size: 'tiny'
			}
			this.apiCall({ destination: 'get_list_products', data: productFilters }).then(obj => {
                this.searching = false
				if (obj.status = 'success' && obj.list) {
					if (obj.list.length == 0) {
						this.searchMessage = 'No matching products found.'
					} else {
						this.searchMessage = 'Select a product to clone...'
						this.searchList = obj.list
                    }
				} else {
					this.searchMessage = obj.message
				}
			}).catch(err => {
                this.searching = false
				this.searchMessage = err
			})			
		},
		cloneProduct(product) {
			this.searchMessage = ''
			product.product_id = ''
			product.upc = product.upc + '-R'
			product.cost = ''
			product.pos_price = ''
			// stringify copy to avoid reactivity
			this.listDetail = JSON.parse(JSON.stringify(product))
		},
		searchSerialNumbers() {
			if (this.searching) { return }
			this.serialNumberSearchMessage = ''
			this.serialNumberSearchResults = []
			if (!this.serialNumberSearch) { return }
			this.searching = true
			let excludeFromSearch = []
			for (var i = 0; i < this.listDetail.serial_numbers.length; i++) {
				if (this.listDetail.serial_numbers[i].serial_number != this.serialNumberSearch) {
					excludeFromSearch.push(this.listDetail.serial_numbers[i])
				}
			}
			this.apiCall({ destination: 'get_list_serial_numbers', data: { on_hand: 1, search_term: this.serialNumberSearch, exclude: excludeFromSearch }}).then(obj => {
				this.searching = false
				if (obj.status == 'error') {
					this.serialNumberSearchMessage = obj.message
				} else if (obj.serial_numbers.length > 0) {
					this.serialNumberSearchMessage = 'Select a serial number...'
					this.serialNumberSearchResults = obj.serial_numbers
				} else {
					this.serialNumberSearchMessage = 'No matching serial numbers found'
				}
			})
		},
		assignSerialNumber(serialNumber) {
			// check if already assigned
			for (var i = 0; i < this.listDetail.serial_numbers.length; i++) {
				if (this.listDetail.serial_numbers[i].serial_number == serialNumber.serial_number) {
					this.serialNumberSearchMessage = 'Serial number already assigned to this rental.'
					return
				}
			}
			this.listDetail.serial_numbers.push(serialNumber)
			// remove from search results
			for (var i = 0; i < this.serialNumberSearchResults.length; i++) {
				if (this.serialNumberSearchResults[i].serial_number == serialNumber.serial_number) {
					this.serialNumberSearchResults.splice(i, 1)
					break
				}
			}
		}
	}
}
</script>

<style lang="scss" scoped>
.product-data {
	margin: 0 0 0.5em 0;
	font-size: 1.1em;
}
.serial-number-results {
	overflow: auto;
	max-height: 20em;
	border: 1px solid #ccc;
	border-radius: 0.25em;
	background-color: #fff;
	margin: 0.5em 0;
}
</style>
